import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Hero = () => {
  const { t } = useTranslation();
  const imageRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll();
  
  const backgroundY = useTransform(scrollY, [0, 500], ['0%', '50%']);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!imageRef.current) return;
      
      const { clientX, clientY } = e;
      const { left, top, width, height } = imageRef.current.getBoundingClientRect();
      
      const x = (clientX - left) / width - 0.5;
      const y = (clientY - top) / height - 0.5;
      
      imageRef.current.style.transform = `
        perspective(1000px)
        rotateY(${x * 5}deg)
        rotateX(${-y * 5}deg)
        translateZ(50px)
      `;
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div ref={containerRef} className="relative h-screen overflow-hidden">
      <motion.div 
        ref={imageRef}
        className="absolute inset-0 bg-cover bg-center transition-transform duration-300 ease-out"
        style={{
          backgroundImage: "url('https://res.cloudinary.com/dansvy9a5/image/upload/v1739113190/1736183457106_2_zifcfe.jpg')",
          y: backgroundY,
          opacity
        }}
        aria-label="Transformative immersion experiences in Mauritius for independent women – AURA IMMERSION"
      />

      <div className="relative h-full flex flex-col items-center justify-center text-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 1,
            ease: [0.25, 0.1, 0.25, 1],
            staggerChildren: 0.1
          }}
          className="relative max-w-4xl mx-auto"
        >
          <motion.h1 
            className="text-5xl md:text-7xl font-serif mb-6 relative text-[#41392e]"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="inline-block relative">
              {t('hero.title')}
              <motion.div 
                className="absolute -left-4 -right-4 h-px bg-gradient-to-r from-transparent via-[#c9ab81] to-transparent"
                initial={{ scaleX: 0, opacity: 0 }}
                animate={{ scaleX: 1, opacity: 1 }}
                transition={{ duration: 1, delay: 0.5 }}
              />
            </span>
            <br />
            <span className="text-[#c9ab81]">{t('hero.subtitle')}</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link to="/program" className="group relative px-8 py-3 overflow-hidden">
              <div className="absolute inset-0 w-0 bg-[#c9ab81] transition-all duration-[750ms] ease-out group-hover:w-full" />
              <span className="relative text-lg font-serif text-[#41392e] group-hover:text-white transition-colors duration-[750ms]">
                {t('hero.cta')}
              </span>
              <div className="absolute inset-0 border border-[#c9ab81] pointer-events-none" />
            </Link>
          </motion.div>
        </motion.div>

        {/* Decorative Elements */}
        <motion.div 
          className="absolute left-10 top-1/2 -translate-y-1/2 w-px h-1/3"
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
        >
          <div className="h-full w-full bg-gradient-to-b from-transparent via-[#c9ab81] to-transparent opacity-30" />
        </motion.div>
        <motion.div 
          className="absolute right-10 top-1/2 -translate-y-1/2 w-px h-1/3"
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
        >
          <div className="h-full w-full bg-gradient-to-b from-transparent via-[#c9ab81] to-transparent opacity-30" />
        </motion.div>

        {/* Luxury Corner Accents */}
        <div className="absolute top-10 left-10 w-20 h-20">
          <motion.div 
            className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-[#c9ab81] to-transparent"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
          />
          <motion.div 
            className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-[#c9ab81] to-transparent"
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
          />
        </div>
        <div className="absolute top-10 right-10 w-20 h-20">
          <motion.div 
            className="absolute top-0 right-0 w-full h-px bg-gradient-to-l from-[#c9ab81] to-transparent"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
          />
          <motion.div 
            className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-[#c9ab81] to-transparent"
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
          />
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2 }}
      >
        <div className="w-px h-16 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-[#c9ab81] to-transparent animate-scroll-indicator" />
        </div>
      </motion.div>
    </div>
  );
};

export default Hero;